import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ShieldCheck, Moon, Sun, MapPinned, AlertCircle, Clock, Trash2, ShoppingCart, Hammer, TrendingUp, Bookmark, Share2, GitCompareArrows } from "lucide-react";
import { AddressSearchBar } from "@/components/AddressSearchBar";
import { PropertyReport } from "@/components/PropertyReport";
import { CompareDialog } from "@/components/report/CompareDialog";
import { ProjectAdvisor, PermitHelp } from "@/components/ProductPages";
import { PaymentResult } from "@/components/Payments";
import { PropertyPreview } from "@/components/PropertyPreview";
import { Dashboard } from "@/components/Dashboard";
import { ServicesBanner } from "@/components/ProAssistance";
import { AuthDialog } from "@/components/AuthDialog";
import { RequestDialog, UserDialog } from "@/components/AdminPanels";
import { AuthProvider, useAuth } from "@/lib/auth";
import { fmtFolio } from "@/lib/permitcheck";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LanguageProvider, useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECENTS_KEY = "permitcheck_recents";

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("pc_theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("pc_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, () => setDark((d) => !d)];
}

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center overflow-hidden rounded-full border border-border" data-testid="language-toggle">
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
            lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const NAV = [
  { key: "search", path: "/property-search", labelKey: "nav.search" },
  { key: "advisor", path: "/project-advisor", labelKey: "nav.advisor" },
  { key: "help", path: "/permit-help", labelKey: "nav.help" },
];

const Header = ({ dark, toggle, view, setView, onSignIn }) => {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navButton = (n, full) => (
    <button
      key={n.key}
      data-testid={`nav-${n.key}`}
      onClick={() => setView(n.key)}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${full ? "flex-1 text-center" : ""} ${view === n.key ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-primary"}`}
    >
      {t(n.labelKey)}
    </button>
  );
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-md pc-noprint">
      <div className="mx-auto max-w-6xl px-4 py-3 sm:px-6">
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => setView("search")} data-testid="brand-logo" className="flex shrink-0 items-center gap-2.5">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-border">
              <img src="/wps-logo.png" alt="Wise Property Search" className="h-9 w-9 object-contain" />
            </div>
            <div className="block leading-none">
              <p className="font-sans text-base font-extrabold tracking-tight sm:text-lg">
                <span style={{ color: dark ? "#FFFFFF" : "#0B2E4E" }}>Wise </span>
                <span style={{ color: "#3AAE5F" }}>Property</span>
              </p>
              <p className="mt-0.5 font-sans text-xs font-semibold tracking-[0.22em] sm:text-sm sm:tracking-[0.28em]" style={{ color: dark ? "#E5E7EB" : "#0B2E4E" }}>
                SEARCH
              </p>
            </div>
          </button>

          <nav className="hidden items-center gap-0.5 overflow-x-auto rounded-full border border-border bg-secondary/50 p-0.5 md:flex" data-testid="primary-nav">
            {NAV.map((n) => navButton(n, false))}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <LangToggle />
            {user ? (
              <div className="flex items-center gap-1.5">
                <button data-testid="nav-dashboard" onClick={() => setView("dashboard")}
                  className={`hidden rounded-full px-3 py-1.5 text-xs font-semibold sm:block ${view === "dashboard" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-primary"}`}>
                  {t("nav.dashboard")}
                </button>
                <button data-testid="sign-out-btn" onClick={logout} className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground/70 hover:text-primary">
                  {t("auth.signOut")}
                </button>
              </div>
            ) : (
              <button data-testid="sign-in-btn" onClick={onSignIn} className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90">
                {t("auth.signIn")}
              </button>
            )}
            <button onClick={toggle} data-testid="theme-toggle" aria-label={t("header.toggleTheme")} className="rounded-full border border-border p-2 text-foreground hover:bg-secondary">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <nav className="mt-2.5 flex items-center gap-1 rounded-full border border-border bg-secondary/50 p-1 md:hidden" data-testid="primary-nav-mobile">
          {NAV.map((n) => navButton(n, true))}
          {user && (
            <button data-testid="nav-dashboard-mobile" onClick={() => setView("dashboard")}
              className={`flex-1 whitespace-nowrap rounded-full px-3 py-1.5 text-center text-xs font-semibold transition-colors ${view === "dashboard" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-primary"}`}>
              {t("nav.dashboard")}
            </button>
          )}
        </nav>
      </div>
    </header>
  );
};

const ProductCards = ({ setView }) => {
  const { t } = useLang();
  return (
    <div className="mx-auto mt-10 grid max-w-5xl gap-4 px-4 sm:px-6 md:grid-cols-3" data-testid="product-cards">
      {[
        ["search", "nav.search", "card.search.desc"],
        ["advisor", "nav.advisor", "card.advisor.desc"],
        ["help", "nav.help", "card.help.desc"],
      ].map(([key, titleKey, descKey]) => (
        <button key={key} data-testid={`product-card-${key}`} onClick={() => setView(key)} className="rounded-xl border border-border bg-card p-5 text-left transition-colors hover:border-primary/40">
          <p className="font-serif text-lg font-semibold text-foreground">{t(titleKey)}</p>
          <p className="mt-1.5 text-sm text-muted-foreground">{t(descKey)}</p>
        </button>
      ))}
    </div>
  );
};

const RecentLookups = ({ items, onPick, onClear }) => {
  const { t } = useLang();
  if (!items.length) return null;
  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <Clock className="h-3 w-3" /> {t("recent.title")}
        </span>
        <button
          data-testid="clear-recents-btn"
          onClick={onClear}
          className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3 w-3" /> {t("recent.clear")}
        </button>
      </div>
      <div data-testid="recent-lookups-list" className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <button
            key={`${it.folio}-${i}`}
            data-testid="recent-lookup-item"
            onClick={() => onPick(it.address)}
            className="group flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-left hover:border-primary/50"
          >
            <MapPinned className="h-3.5 w-3.5 text-accent" />
            <span className="text-xs font-medium text-foreground">{it.address.split(",")[0]}</span>
            {it.zoning && (
              <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[9px] font-semibold text-muted-foreground">
                {it.zoning}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const Benefits = () => {
  const { t } = useLang();
  const cards = [
    { icon: ShoppingCart, title: t("benefits.buy.title"), desc: t("benefits.buy.desc"), testid: "benefit-buy" },
    { icon: Hammer, title: t("benefits.build.title"), desc: t("benefits.build.desc"), testid: "benefit-build" },
    { icon: TrendingUp, title: t("benefits.invest.title"), desc: t("benefits.invest.desc"), testid: "benefit-invest" },
  ];
  return (
    <section className="border-b border-border bg-secondary/20 pc-noprint" data-testid="benefits-section">
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        <h2 className="font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {t("benefits.heading")}
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {cards.map((c) => (
            <div
              key={c.testid}
              data-testid={c.testid}
              className="group rounded-xl border border-border bg-card p-6 transition-colors hover:border-primary/40"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/5 text-primary transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                <c.icon className="h-5 w-5" />
              </div>
              <p className="mt-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-accent">{c.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SavedReports = ({ onLoad }) => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const refresh = useCallback(() => {
    axios.get(`${API}/saved-reports`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener("pc-saved", refresh);
    return () => window.removeEventListener("pc-saved", refresh);
  }, [refresh]);

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/saved-reports/${id}`);
      setSelected((s) => s.filter((x) => x !== id));
      refresh();
    } catch {
      /* noop */
    }
  };

  const share = async (id, e) => {
    e.stopPropagation();
    const link = `${window.location.origin}/?report=${id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("report.linkCopied"));
    } catch {
      toast.success(link);
    }
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 2 ? [...s, id] : [s[1], id]));
  };

  const load = async (id) => {
    try {
      const { data } = await axios.get(`${API}/saved-reports/${id}`);
      onLoad(data);
    } catch {
      /* noop */
    }
  };

  if (!items.length) return null;
  return (
    <div className="mt-5">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <Bookmark className="h-3 w-3" /> {t("saved.title")}
        </span>
        {selected.length === 2 && (
          <button
            data-testid="compare-btn"
            onClick={() => setCompareOpen(true)}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold text-primary-foreground"
          >
            <GitCompareArrows className="h-3 w-3" /> {t("cmp.button")} (2)
          </button>
        )}
      </div>
      <div data-testid="saved-reports-list" className="flex flex-wrap gap-2">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid="saved-report-item"
            onClick={() => load(it.id)}
            className={`group flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 ${selected.includes(it.id) ? "border-accent bg-accent/10" : "border-primary/25 bg-primary/5 hover:border-primary/50"}`}
          >
            <button onClick={(e) => toggleSelect(it.id, e)} data-testid="compare-select" title="Compare" className="text-muted-foreground hover:text-accent">
              <GitCompareArrows className={`h-3.5 w-3.5 ${selected.includes(it.id) ? "text-accent" : ""}`} />
            </button>
            <span className="text-xs font-medium text-foreground">{(it.address || "").split(",")[0]}</span>
            {it.zoning && (
              <span className="rounded bg-card px-1.5 py-0.5 font-mono text-[9px] font-semibold text-muted-foreground">{it.zoning}</span>
            )}
            <button onClick={(e) => share(it.id, e)} data-testid="share-saved-btn" title="Share" className="ml-1 text-muted-foreground hover:text-primary">
              <Share2 className="h-3 w-3" />
            </button>
            <button onClick={(e) => remove(it.id, e)} data-testid="delete-saved-btn" title="Delete" className="text-muted-foreground hover:text-destructive">
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <CompareDialog ids={selected} open={compareOpen} onOpenChange={setCompareOpen} />
    </div>
  );
};

const STAGES = [
  { key: "new", label: "New", dot: "bg-amber-500", ring: "border-amber-500/40" },
  { key: "contacted", label: "Contacted", dot: "bg-blue-500", ring: "border-blue-500/40" },
  { key: "qualified", label: "Qualified", dot: "bg-violet-500", ring: "border-violet-500/40" },
  { key: "won", label: "Won", dot: "bg-emerald-500", ring: "border-emerald-500/40" },
  { key: "lost", label: "Lost", dot: "bg-rose-500", ring: "border-rose-500/40" },
];
const STAGE_KEYS = STAGES.map((s) => s.key);
const normalizeStage = (s) => (s === "resolved" ? "won" : STAGE_KEYS.includes(s) ? s : "new");

const AdminInbox = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("pc_admin_token") || "");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selReq, setSelReq] = useState(null);
  const [selUser, setSelUser] = useState(null);
  const [crmView, setCrmView] = useState("kanban");

  const moveStage = async (id, status) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    try {
      await axios.patch(`${API}/admin/requests/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { loadRequests(); }
  };

  const loadRequests = (tk) => {
    const cfg = { headers: { Authorization: `Bearer ${tk || token}` } };
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (statusFilter !== "all" && crmView === "list") params.set("status", statusFilter);
    axios.get(`${API}/assistance-requests?${params}`, cfg).then((r) => setItems(r.data || []))
      .catch((e) => { if (e.response?.status === 401) { sessionStorage.removeItem("pc_admin_token"); setToken(""); } });
  };

  const load = (tk) => {
    const cfg = { headers: { Authorization: `Bearer ${tk}` } };
    loadRequests(tk);
    axios.get(`${API}/payments`, cfg).then((r) => setPayments(r.data || [])).catch(() => {});
    axios.get(`${API}/admin/users`, cfg).then((r) => setUsers(r.data || [])).catch(() => {});
    axios.get(`${API}/admin/stats`, cfg).then((r) => setStats(r.data || null)).catch(() => {});
  };

  useEffect(() => { if (token) load(token); }, [token]);
  useEffect(() => { if (token) loadRequests(); }, [crmView]); // eslint-disable-line

  const login = async () => {
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      sessionStorage.setItem("pc_admin_token", data.token);
      setToken(data.token);
      setPw("");
    } catch { setErr("Incorrect password."); }
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-sm px-4 py-24" data-testid="admin-login">
        <h1 className="mb-1 font-serif text-2xl font-semibold text-foreground">Admin Inbox</h1>
        <p className="mb-5 text-sm text-muted-foreground">Enter the admin password to continue.</p>
        <Input type="password" data-testid="admin-password-input" value={pw} placeholder="Password"
          onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} className="mb-3" />
        {err && <p className="mb-3 text-sm text-destructive" data-testid="admin-login-error">{err}</p>}
        <Button className="w-full" data-testid="admin-login-btn" onClick={login}>Unlock Inbox</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="admin-inbox">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="mb-1 font-serif text-3xl font-semibold text-foreground">Assistance Inbox</h1>
          <p className="text-sm text-muted-foreground">{items.length} request(s) · {payments.length} payment(s).</p>
        </div>
        <Button variant="outline" size="sm" data-testid="admin-logout-btn"
          onClick={() => { sessionStorage.removeItem("pc_admin_token"); setToken(""); }}>Log out</Button>
      </div>

      {stats && (
        <div className="mb-8 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="admin-stats">
          {[["Total Users", stats.total_users], ["New (7d)", stats.new_users], ["Active (7d)", stats.active_users],
            ["Searches", stats.property_searches], ["Reports", stats.reports_viewed], ["Leads", stats.leads_generated]].map(([label, val]) => (
            <div key={label} className="rounded-xl border border-border bg-card p-3 text-center">
              <p className="font-serif text-2xl font-semibold text-foreground">{val}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-accent">Users</h2>
      <div className="mb-8 space-y-2" data-testid="admin-users">
        {users.map((u) => (
          <div key={u.id} data-testid="user-item" onClick={() => setSelUser(u.id)} className="flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40">
            <div>
              <p className="font-semibold text-foreground">{u.first_name} · <span className="font-normal text-muted-foreground">{u.email}</span>{u.phone && <span className="font-normal text-muted-foreground"> · {u.phone}</span>}</p>
              <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                joined {(u.created_at || "").slice(0, 10)} · searches {u.counts?.searches || 0} · reports {u.counts?.reports_viewed || 0} · ask {u.counts?.ask_used || 0} · advisor {u.counts?.advisor_used || 0} · permit {u.counts?.permithelp_used || 0} · leads {u.counts?.assistance_requests || 0}
              </p>
            </div>
            <span className="font-mono text-[10px] text-primary">Review with AI →</span>
          </div>
        ))}
        {users.length === 0 && <p className="text-sm text-muted-foreground">No users yet.</p>}
      </div>

      <h2 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-accent">Payments</h2>
      <div className="mb-8 space-y-2" data-testid="admin-payments">
        {payments.map((p, i) => (
          <div key={i} data-testid="payment-item" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div>
              <p className="font-semibold text-foreground">{p.product_label} · <span className="font-mono text-emerald-600 dark:text-emerald-400">${((p.amount || 0) / 100).toFixed(2)}</span></p>
              <p className="mt-0.5 font-mono text-xs text-primary">{p.address || "—"} · {p.folio || "—"}</p>
            </div>
            <span className="font-mono text-[10px] text-muted-foreground">{p.updated_at}</span>
          </div>
        ))}
        {payments.length === 0 && <p className="text-sm text-muted-foreground">No payments yet.</p>}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-accent">Leads · CRM</h2>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-full border border-border bg-secondary/50 p-0.5" data-testid="crm-view-toggle">
            {[["kanban", "Kanban"], ["list", "List"]].map(([k, lbl]) => (
              <button key={k} data-testid={`crm-view-${k}`} onClick={() => setCrmView(k)}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold ${crmView === k ? "bg-primary text-primary-foreground" : "text-foreground/70"}`}>{lbl}</button>
            ))}
          </div>
          <Input data-testid="admin-search" value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadRequests()} placeholder="Search name, email, property…" className="h-8 w-56" />
          {crmView === "list" && (
            <select data-testid="admin-status-filter" value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setTimeout(loadRequests, 0); }}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs">
              {["all", ...STAGE_KEYS].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <Button size="sm" variant="outline" className="h-8" data-testid="admin-search-btn" onClick={() => loadRequests()}>Search</Button>
        </div>
      </div>

      {/* Pipeline metrics */}
      <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="crm-metrics">
        {STAGES.map((s) => {
          const c = items.filter((it) => normalizeStage(it.status) === s.key).length;
          return (
            <div key={s.key} className="rounded-xl border border-border bg-card p-3 text-center" data-testid={`crm-metric-${s.key}`}>
              <p className="font-serif text-2xl font-semibold text-foreground">{c}</p>
              <p className="flex items-center justify-center gap-1 font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${s.dot}`} />{s.label}
              </p>
            </div>
          );
        })}
        {(() => {
          const won = items.filter((it) => normalizeStage(it.status) === "won").length;
          const rate = items.length ? Math.round((won / items.length) * 100) : 0;
          return (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-center" data-testid="crm-metric-conversion">
              <p className="font-serif text-2xl font-semibold text-primary">{rate}%</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">Conversion</p>
            </div>
          );
        })()}
      </div>

      {crmView === "kanban" ? (
        <div className="flex gap-3 overflow-x-auto pb-3" data-testid="crm-kanban">
          {STAGES.map((stage) => {
            const leads = items.filter((it) => normalizeStage(it.status) === stage.key);
            return (
              <div key={stage.key} data-testid={`kanban-column-${stage.key}`} className="flex w-64 shrink-0 flex-col rounded-xl border border-border bg-secondary/30">
                <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                  <span className={`inline-block h-2 w-2 rounded-full ${stage.dot}`} />
                  <span className="text-xs font-semibold text-foreground">{stage.label}</span>
                  <span className="ml-auto rounded-full bg-background px-2 py-0.5 font-mono text-[10px] text-muted-foreground">{leads.length}</span>
                </div>
                <div className="flex flex-col gap-2 p-2">
                  {leads.map((it) => (
                    <div key={it.id} data-testid="kanban-card" onClick={() => setSelReq(it)}
                      className={`cursor-pointer rounded-lg border bg-card p-3 transition-colors hover:border-primary/50 ${stage.ring}`}>
                      <p className="truncate text-sm font-semibold text-foreground">{it.name}</p>
                      <p className="mt-0.5 truncate font-mono text-[10px] text-primary">{it.request_type}</p>
                      <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">{it.address || "—"}</p>
                      <p className="mt-1 line-clamp-2 text-[11px] text-foreground/80">{it.description}</p>
                      <select value={normalizeStage(it.status)} data-testid="kanban-stage-select"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => { e.stopPropagation(); moveStage(it.id, e.target.value); }}
                        className="mt-2 h-7 w-full rounded-md border border-input bg-background px-1.5 text-[11px]">
                        {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </div>
                  ))}
                  {leads.length === 0 && <p className="px-1 py-3 text-center text-[11px] text-muted-foreground">—</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid="assistance-item" onClick={() => setSelReq(it)} className="cursor-pointer rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-foreground">
                  {it.name} · <span className="font-normal text-muted-foreground">{it.email}</span>
                  {it.phone && <span className="font-normal text-muted-foreground"> · {it.phone}</span>}
                </p>
                <div className="flex items-center gap-2">
                  {it.status && (() => {
                    const st = normalizeStage(it.status);
                    const cls = st === "won" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                      : st === "lost" ? "bg-rose-500/15 text-rose-600 dark:text-rose-400"
                      : st === "qualified" ? "bg-violet-500/15 text-violet-600 dark:text-violet-400"
                      : st === "contacted" ? "bg-blue-500/15 text-blue-600 dark:text-blue-400"
                      : "bg-amber-500/15 text-amber-600";
                    return <span data-testid="status-badge" className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide ${cls}`}>{st}</span>;
                  })()}
                  <span className="font-mono text-[10px] text-muted-foreground">{(it.created_at || "").slice(0, 10)}</span>
                </div>
              </div>
              <p className="mt-1 font-mono text-xs text-primary">{it.request_type} · {it.address || "—"} · {it.folio || "—"}</p>
              <p className="mt-2 line-clamp-2 whitespace-pre-wrap text-sm text-foreground">{it.description}</p>
              <p className="mt-1 font-mono text-[10px] text-accent">Open to reply with AI →</p>
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-muted-foreground">No requests yet.</p>}
        </div>
      )}

      {selReq && <RequestDialog req={selReq} token={token} open={!!selReq} onOpenChange={(o) => !o && setSelReq(null)} onChanged={() => { loadRequests(); }} />}
      {selUser && <UserDialog userId={selUser} token={token} open={!!selUser} onOpenChange={(o) => !o && setSelUser(null)} />}
    </div>
  );
};

const pathToView = (p) =>
  p.includes("/payment/success") ? "psuccess"
  : p.includes("/payment/cancel") ? "pcancel"
  : p.includes("project-advisor") ? "advisor"
  : p.includes("permit-help") ? "help"
  : p.includes("my-propertycheck") ? "dashboard"
  : p.includes("admin") ? "admin"
  : "search";

const SignInGate = ({ onSignIn }) => {
  const { t } = useLang();
  return (
    <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="signin-gate">
      <p className="font-serif text-2xl font-semibold text-foreground">{t("preview.locked")}</p>
      <p className="mt-2 text-sm text-muted-foreground">{t("preview.support")}</p>
      <Button size="lg" className="mt-5" data-testid="gate-signin-btn" onClick={onSignIn}>{t("preview.cta")}</Button>
      <p className="mt-2 text-[11px] text-muted-foreground">{t("auth.noCard")}</p>
    </div>
  );
};

function Home() {
  const { t, lang } = useLang();
  const { user, token, track, authOpen, setAuthOpen, openAuth } = useAuth();
  const [dark, toggle] = useTheme();
  const [view, setViewState] = useState(() => pathToView(window.location.pathname));
  const setView = (v) => {
    const paths = { search: "/property-search", advisor: "/project-advisor", help: "/permit-help", admin: "/admin", dashboard: "/my-propertycheck" };
    window.history.pushState({}, "", paths[v] || "/property-search");
    setViewState(v);
    window.scrollTo(0, 0);
  };
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [recents, setRecents] = useState([]);

  useEffect(() => {
    try {
      setRecents(JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]"));
    } catch {
      setRecents([]);
    }
    // Shared-report deep link: /?report=<id>
    const shared = new URLSearchParams(window.location.search).get("report");
    if (shared) {
      axios.get(`${API}/saved-reports/${shared}`).then((r) => { setError(null); setReport(r.data); }).catch(() => setError("This shared report is no longer available."));
    }
    const onPop = () => {
      setViewState(pathToView(window.location.pathname));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const pushRecent = useCallback((r) => {
    const entry = { address: r.address, folio: r.folio, zoning: r.zoning, municipality: r.municipality };
    setRecents((prev) => {
      const next = [entry, ...prev.filter((p) => p.folio !== r.folio)].slice(0, 6);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const search = useCallback(
    async (address) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.post(`${API}/property/lookup`, { address });
        if (!data.found) {
          setReport(null);
          const msg = data.error || t("error.notFound");
          setError(msg);
          toast.error(msg);
        } else {
          setReport(data);
          pushRecent(data);
          toast.success(`Folio ${fmtFolio(data.folio)} · ${data.jurisdiction}`);
        }
      } catch (e) {
        setError(t("error.failed"));
        toast.error(t("toast.failed"));
      } finally {
        setLoading(false);
      }
    },
    [pushRecent, t]
  );

  const clearRecents = () => {
    localStorage.removeItem(RECENTS_KEY);
    setRecents([]);
  };

  useEffect(() => {
    if (report && report.folio && token) {
      axios.post(`${API}/me/properties`, { report }).catch(() => {});
    }
  }, [report, token]);

  return (
    <div className="min-h-screen bg-background">
      <Header dark={dark} toggle={toggle} view={view} setView={setView} onSignIn={() => setAuthOpen(true)} />

      {view === "advisor" && <ProjectAdvisor report={report} onSearch={search} />}
      {view === "help" && <PermitHelp report={report} onSearch={search} />}
      {view === "dashboard" && (user ? <Dashboard onOpenProperty={(r) => { setReport(r); setView("search"); }} /> : <SignInGate onSignIn={openAuth} />)}
      {view === "admin" && <AdminInbox />}
      {view === "psuccess" && <PaymentResult mode="success" onDone={() => setView("search")} />}
      {view === "pcancel" && <PaymentResult mode="cancel" onDone={() => setView("search")} />}

      {view === "search" && (<>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border pc-noprint">
        <div className="absolute inset-0 pc-grid opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:py-20">
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          <div>
          <p className="mb-3 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-accent">
            {t("hero.eyebrow")}
          </p>
          <h1 className="max-w-3xl font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {t("hero.title1")}
            <br />
            <span className="text-primary/70 dark:text-primary/90">{t("hero.title2")}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">{t("hero.subtitle")}</p>

          <div className="mt-8 max-w-3xl">
            <AddressSearchBar onSearch={search} loading={loading} />
            <p className="mt-3 text-sm text-muted-foreground">{t("search.valueprop")}</p>
            <p className="mt-2 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
              {t("search.categories")}
            </p>
            <p className="mt-2 text-xs font-medium text-muted-foreground" data-testid="hero-nocard">{t("auth.noCard")}</p>
            <RecentLookups items={recents} onPick={search} onClear={clearRecents} />
            <SavedReports onLoad={(r) => { setError(null); setReport(r); }} />
          </div>
          </div>

          <div className="relative" data-testid="hero-image">
            <div className="absolute -inset-3 rounded-[1.75rem] bg-accent/10 blur-2xl" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-3xl ring-1 ring-border shadow-2xl shadow-primary/15">
              <img
                src="/hero-home.png"
                alt="A person using Wise Property Search on a tablet to review a property before deciding"
                loading="eager"
                className="h-60 w-full object-cover sm:h-80 lg:h-[480px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/25 via-transparent to-transparent" aria-hidden="true" />
            </div>
          </div>
          </div>
        </div>
      </section>

      {!report && <Benefits />}
      {!report && <ProductCards setView={setView} />}
      {!report && <ServicesBanner />}

      {/* Results */}
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <AnimatePresence mode="wait">
          {error && !report && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-5"
              data-testid="lookup-error"
            >
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div>
                <p className="font-medium text-foreground">{t("error.title")}</p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            </motion.div>
          )}

          {report && (
            <motion.div key={report.folio + lang} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {!user && (
                <div data-testid="save-receive-bar" className="mb-4 flex flex-col items-start gap-3 rounded-xl border border-primary/25 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-serif text-base font-semibold text-foreground">{t("save.title")}</p>
                    <p className="text-sm text-muted-foreground">{t("save.desc")}</p>
                  </div>
                  <Button data-testid="save-receive-btn" onClick={openAuth}>{t("save.cta")}</Button>
                </div>
              )}
              {user && (
                <div data-testid="saved-bar" className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-700 dark:text-emerald-400">
                  <Bookmark className="h-4 w-4" />{t("save.saved")}
                </div>
              )}
              <PropertyReport report={report} />
            </motion.div>
          )}

          {!report && !error && (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="rounded-xl border border-dashed border-border bg-card/50 p-12 text-center"
            >
              <MapPinned className="mx-auto h-10 w-10 text-muted-foreground/50" />
              <p className="mt-4 font-serif text-xl text-foreground">{t("empty.title")}</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t("empty.subtitle")}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      </>)}

      {/* Footer */}
      <footer className="mt-8 border-t border-border bg-secondary/30 py-10 pc-noprint">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <p className="max-w-3xl text-sm text-muted-foreground">{t("footer.disclaimer")}</p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs">
            <a href="https://gisweb.miamidade.gov/arcgis/rest/services/MD_LandInformation/MapServer" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Miami-Dade Land Information
            </a>
            <a href="https://gis.miami.gov/gis/rest/services/Zoning/ZoningMiami21/MapServer" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              City of Miami · Miami 21 Zoning
            </a>
            <a href="https://www.miami21.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Miami 21 Code
            </a>
            <button onClick={() => setView("admin")} data-testid="admin-link" className="text-primary hover:underline">
              Admin Inbox
            </button>
          </div>
        </div>
      </footer>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => setAuthOpen(false)} />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Home />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
